import { Fragment } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    alpha,
    Box,
    Button,
    Chip,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { docPath } from "../../config/documentationContent";

function VideoPlaceholder() {
    return (
        <Box
            sx={{
                height: 280,
                border: "2px dashed",
                borderColor: "primary.main",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha("#e8c97e", 0.04),
                mb: 3,
            }}
        >
            <Typography
                variant="body1"
                sx={{ color: "primary.main", fontFamily: '"DM Sans", sans-serif', textAlign: "center", px: 2 }}
            >
                📹 Video walkthrough — add your video embed here
            </Typography>
        </Box>
    );
}

function ScreenshotPlaceholder() {
    return (
        <Box
            sx={{
                height: 160,
                border: "2px dashed",
                borderColor: "secondary.main",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha("#4ecdc4", 0.04),
                mb: 3,
            }}
        >
            <Typography
                variant="body1"
                sx={{ color: "secondary.main", fontFamily: '"DM Sans", sans-serif', textAlign: "center", px: 2 }}
            >
                🖼 Screenshots — add your images here
            </Typography>
        </Box>
    );
}

function WorkflowDiagram({ steps }) {
    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down("md"));

    const arrow = isNarrow ? (
        <KeyboardArrowDownIcon sx={{ color: "primary.main", my: 0.25 }} />
    ) : (
        <ArrowForwardIcon sx={{ color: "primary.main", mx: 0.75, flexShrink: 0, fontSize: 22 }} />
    );

    return (
        <Box sx={{ mb: 1, overflowX: isNarrow ? "visible" : "auto", pb: isNarrow ? 0 : 0.5 }}>
            <Stack
                direction={isNarrow ? "column" : "row"}
                alignItems="center"
                spacing={isNarrow ? 0 : 0}
                sx={{ width: "100%", minWidth: isNarrow ? "auto" : 720 }}
            >
                {steps.map((label, i) => (
                    <Fragment key={label}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 1.75,
                                width: isNarrow ? "100%" : "auto",
                                flex: isNarrow ? "none" : "1 1 0",
                                minWidth: isNarrow ? "auto" : 120,
                                border: "1px solid",
                                borderColor: i === steps.length - 1 ? alpha("#e8c97e", 0.45) : alpha("#4ecdc4", 0.35),
                                bgcolor: i === steps.length - 1 ? alpha("#e8c97e", 0.08) : alpha("#4ecdc4", 0.06),
                                textAlign: "center",
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    display: "block",
                                    color: "text.secondary",
                                    fontFamily: '"DM Sans", sans-serif',
                                    fontWeight: 600,
                                    mb: 0.5,
                                }}
                            >
                                Step {i + 1}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "text.primary",
                                    fontFamily: '"DM Sans", sans-serif',
                                    fontWeight: 500,
                                    lineHeight: 1.45,
                                }}
                            >
                                {label}
                            </Typography>
                        </Paper>
                        {i < steps.length - 1 && arrow}
                    </Fragment>
                ))}
            </Stack>
        </Box>
    );
}

function NoteCallout({ text }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                mb: 3,
                border: "1px solid",
                borderColor: alpha("#4ecdc4", 0.25),
                bgcolor: alpha("#4ecdc4", 0.06),
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Chip
                    icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
                    label="Note"
                    size="small"
                    sx={{
                        bgcolor: "secondary.main",
                        color: "#0a0c0f",
                        fontFamily: '"DM Sans", sans-serif',
                        fontWeight: 700,
                        flexShrink: 0,
                    }}
                />
                <Typography variant="body2" sx={{ color: "text.primary", pt: 0.25 }}>
                    {text}
                </Typography>
            </Stack>
        </Paper>
    );
}

function TipsCallout({ text }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                mt: 3,
                border: "1px solid",
                borderColor: alpha("#e8c97e", 0.25),
                bgcolor: alpha("#e8c97e", 0.06),
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Chip
                    icon={<LightbulbOutlinedIcon sx={{ fontSize: 16 }} />}
                    label="Tips & Notes"
                    size="small"
                    sx={{
                        bgcolor: "primary.main",
                        color: "#0a0c0f",
                        fontFamily: '"DM Sans", sans-serif',
                        fontWeight: 700,
                        flexShrink: 0,
                    }}
                />
                <Typography variant="body2" sx={{ color: "text.primary", pt: 0.25 }}>
                    {text}
                </Typography>
            </Stack>
        </Paper>
    );
}

export function TocNavList({ items, activeId, onItemClick, compact }) {
    if (items.length === 0) {
        return (
            <Typography variant="body2" sx={{ color: "text.secondary", px: 1, py: 1, fontFamily: '"DM Sans", sans-serif' }}>
                No sections match your search.
            </Typography>
        );
    }

    return (
        <Stack spacing={0.25} component="nav" aria-label="Table of contents">
            {items.map((item) => {
                const active = activeId === item.id;
                return (
                    <Button
                        key={item.id}
                        component={RouterLink}
                        to={docPath(item.id)}
                        onClick={onItemClick}
                        sx={{
                            justifyContent: "flex-start",
                            textAlign: "left",
                            color: active ? "primary.main" : "text.secondary",
                            fontFamily: '"DM Sans", sans-serif',
                            fontSize: compact ? 14 : 13,
                            fontWeight: active ? 600 : 400,
                            py: compact ? 0.75 : 0.6,
                            px: 1,
                            minHeight: 0,
                            lineHeight: 1.4,
                            borderLeft: active ? "2px solid" : "2px solid transparent",
                            borderColor: active ? "primary.main" : "transparent",
                            borderRadius: 0,
                            bgcolor: active ? alpha("#e8c97e", 0.1) : "transparent",
                            "&:hover": {
                                color: "primary.main",
                                bgcolor: alpha("#e8c97e", 0.06),
                            },
                        }}
                    >
                        {item.label}
                    </Button>
                );
            })}
        </Stack>
    );
}

export function DocSection({ section, index }) {
    return (
        <Box
            component="article"
            sx={{
                borderTop: `1px solid ${alpha("#e8c97e", 0.35)}`,
                pt: { xs: 3, md: 4 },
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Chip
                    label={String(index + 1).padStart(2, "0")}
                    size="small"
                    sx={{
                        bgcolor: "primary.main",
                        color: "#0a0c0f",
                        fontFamily: '"DM Sans", sans-serif',
                        fontWeight: 700,
                        fontSize: 12,
                        height: 28,
                        minWidth: 36,
                        borderRadius: "4px",
                    }}
                />
                <Typography variant="h4" sx={{ color: "primary.main", m: 0 }}>
                    {section.title}
                </Typography>
            </Stack>
            <Typography variant="body1" sx={{ color: "text.secondary", mb: 3, maxWidth: 720 }}>
                {section.description}
            </Typography>
            {section.note && <NoteCallout text={section.note} />}
            {section.workflow?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{ color: "text.primary", mb: 1.5, fontFamily: '"DM Sans", sans-serif' }}
                    >
                        How the workflow works
                    </Typography>
                    <WorkflowDiagram steps={section.workflow} />
                </Box>
            )}
            <Typography
                variant="subtitle2"
                sx={{ color: "text.primary", mb: 1.5, fontFamily: '"DM Sans", sans-serif' }}
            >
                {section.stepsTitle || "Step-by-step"}
            </Typography>
            <List
                dense
                sx={{
                    pl: 0,
                    mb: 3,
                    "& .MuiListItem-root": {
                        alignItems: "flex-start",
                        pl: 0,
                        py: 0.75,
                    },
                }}
            >
                {section.steps.map((step, i) => (
                    <ListItem key={i} disableGutters>
                        <ListItemText
                            primary={
                                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                    <Box
                                        sx={{
                                            minWidth: 28,
                                            height: 28,
                                            borderRadius: "4px",
                                            bgcolor: alpha("#4ecdc4", 0.12),
                                            color: "secondary.main",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontFamily: '"DM Sans", sans-serif',
                                            fontWeight: 700,
                                            fontSize: 13,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {i + 1}
                                    </Box>
                                    <Typography variant="body2" sx={{ color: "text.primary", pt: 0.35 }}>
                                        {step}
                                    </Typography>
                                </Stack>
                            }
                        />
                    </ListItem>
                ))}
            </List>
            <VideoPlaceholder />
            <ScreenshotPlaceholder />
            {section.dashboardCards?.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{ color: "text.primary", mb: 1.5, fontFamily: '"DM Sans", sans-serif' }}
                    >
                        Dashboard cards explained
                    </Typography>
                    <Stack spacing={1.5}>
                        {section.dashboardCards.map((card) => (
                            <Paper
                                key={card.title}
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    bgcolor: alpha("#4ecdc4", 0.04),
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{ color: "secondary.main", fontFamily: '"DM Sans", sans-serif', mb: 0.5 }}
                                >
                                    {card.title}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                    {card.description}
                                </Typography>
                            </Paper>
                        ))}
                    </Stack>
                </Box>
            )}
            {section.tips && <TipsCallout text={section.tips} />}
        </Box>
    );
}
